import {
  Card,
  TextInputField,
  GreyGrey10,
  GreyGrey60,
  Primary14SemiBold,
  Primary13Regular,
  Primary13SemiBold,
  Box,
  Flex,
  Text
} from '@sequencehq/core-components'
import { ReactNode } from 'react'
import { FormFieldConfig, useForm } from '@sequencehq/utils'
import { required } from '@sequencehq/utils/validation'
import { NetsuiteSettingsFields } from 'Integrations/integrationsConfig/netsuite/netsuite.integration.config'

type Props = {
  existingSettings: NetsuiteSettingsFields
  showValidationErrors: boolean
  onChange: (settings: NetsuiteSettingsFields) => void
  onValidationStateChange: (isValid: boolean) => void
}

type FieldGroupProps = {
  title: string
  children: ReactNode
}

const FieldGroup = ({ title, children }: FieldGroupProps) => (
  <Flex
    backgroundColor={GreyGrey10}
    borderRadius="6px"
    flexDirection="column"
    width="100%"
    p="16px"
    gap="16px"
  >
    <Text {...Primary13SemiBold}>{title}</Text>
    {children}
  </Flex>
)

type IdFieldProps = {
  label: string
  name: keyof NetsuiteSettingsFields
  field: FormFieldConfig<NetsuiteSettingsFields>
}

const IdField = ({ label, name, field }: IdFieldProps) => (
  <TextInputField
    label={label}
    data-testid={`netsuiteSettings.${name}`}
    value={field.value}
    onChange={event => field.onChange(event.target.value)}
    validationErrors={field.validationErrors}
    fullWidth
    autoComplete="off"
  />
)

const NetsuiteSettingsCard = ({
  existingSettings,
  showValidationErrors,
  onChange,
  onValidationStateChange
}: Props) => {
  const { fields } = useForm({
    value: existingSettings,
    showValidationErrors,
    fieldConfiguration: [
      { property: 'subsidiaryId', validation: [required] },
      { property: 'defaultItemIncomeAccountId', validation: [required] },
      { property: 'defaultTaxScheduleId', validation: [required] },
      { property: 'prepaidCreditItemId', validation: [] },
      { property: 'commitmentItemId', validation: [] },
      { property: 'creditGrantItemId', validation: [] },
      { property: 'salesTaxItemId', validation: [] },
      { property: 'deferredRevenueItemId', validation: [] }
    ],
    onChange,
    onValidationStateChange
  })

  return (
    <Card data-testid="netsuiteSettings.card">
      <Flex flexDirection="column" gap="24px">
        <Box>
          <Text {...Primary14SemiBold}>Settings</Text>
          <Text {...Primary13Regular} mt="8px" color={GreyGrey60}>
            You can change these settings without re-entering your credentials.
          </Text>
        </Box>

        <FieldGroup title="Defaults for exported invoices">
          <IdField
            label="Subsidiary ID"
            name="subsidiaryId"
            field={fields.subsidiaryId}
          />
          <IdField
            label="Default item income account ID"
            name="defaultItemIncomeAccountId"
            field={fields.defaultItemIncomeAccountId}
          />
          <IdField
            label="Default tax schedule ID"
            name="defaultTaxScheduleId"
            field={fields.defaultTaxScheduleId}
          />
        </FieldGroup>

        <FieldGroup title="Map invoice line types to NetSuite items">
          <IdField
            label="Prepaid credit item ID"
            name="prepaidCreditItemId"
            field={fields.prepaidCreditItemId}
          />
          <IdField
            label="Commitment item ID"
            name="commitmentItemId"
            field={fields.commitmentItemId}
          />
          <IdField
            label="Credit grant item ID"
            name="creditGrantItemId"
            field={fields.creditGrantItemId}
          />
          <IdField
            label="Sales tax item ID"
            name="salesTaxItemId"
            field={fields.salesTaxItemId}
          />
          <IdField
            label="Deferred revenue item ID"
            name="deferredRevenueItemId"
            field={fields.deferredRevenueItemId}
          />
        </FieldGroup>
      </Flex>
    </Card>
  )
}

export default NetsuiteSettingsCard
